/**
 * MapReportPreview.js
 * ---------------------------------------------------------------------
 * Compact glass panel that sits over the map (bottom-left, where the
 * ParliamentPlot lives) once a constituency is selected. The two are
 * mutually exclusive: the parliament plot only shows at constituency
 * level and vanishes as soon as a seat is selected, freeing this slot.
 *
 *   ┌─────────────────────────┐
 *   │  ░░ glass report preview │  ← top half: frosted window onto the
 *   │  ░░ (click to open full) │     live report; click opens full view
 *   ├─────────────────────────┤
 *   │  [ Default ] [ Custom ] │  ← bottom half: tabbed selector
 *   │  Report  [ Political ▾ ] │     Default = existing reports (dropdown)
 *   │  → Click to see a report │     Custom  = placeholder for now
 *   │    on {constituency}     │
 *   └─────────────────────────┘
 *
 * Report-type rules (which reports are offered for a given seat) will be
 * layered onto the Default dropdown / Custom tab later.
 */
import React, { useState } from "react";
import { Box, Typography, Select, MenuItem, IconButton } from "@mui/material";
import OpenInFullRoundedIcon from "@mui/icons-material/OpenInFullRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ConstituencyReportView from "./ConstituencyReportView";
import BusinessReportView from "./BusinessReportView";
import HolidayTaxReportView from "./HolidayTaxReportView";
import MotabilityReportView from "./MotabilityReportView";
import { HOLIDAY_TAX_CLIENTS } from "./utils/polAggregators";

// Match MapToolbar's glass token so this panel reads as part of the map chrome.
const GLASS = {
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(12px)",
  WebkitBackdropFilter: "blur(12px)",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.06)",
  borderRadius: "12px",
};
const ACCENT = "#0f9fab";
const ACCENT_DARK = "#0b7f88";
const TEXT_FAINT = "#9ca3af";

// The report views render at fixed A4 dimensions; scale to the panel width.
const REPORT_W = 794;
const REPORT_H = 1123;
const PANEL_W = 212;
const PREVIEW_H = 150;
const SCALE = PANEL_W / REPORT_W;

// The "Default" tab's report types — the existing constituency reports.
// Gating mirrors ReportPage: Holiday Tax ("client") only for holiday-tax
// clients, Motability temporarily hidden; Political + Business always shown.
const DEFAULT_REPORTS = [
  { key: "default", label: "Political" },
  { key: "business", label: "Business" },
  { key: "client", label: "Holiday Tax", holidayTaxOnly: true },
  { key: "motability", label: "Motability", hidden: true },
];

function renderReport(reportType, { constName, areaCode, extDataBlurb, demographics, mapImages }) {
  switch (reportType) {
    case "business":
      return <BusinessReportView constName={constName} areaCode={areaCode} />;
    case "client":
      return (
        <HolidayTaxReportView constName={constName} areaCode={areaCode} majority={extDataBlurb?.majority} />
      );
    case "motability":
      return <MotabilityReportView constName={constName} areaCode={areaCode} />;
    case "default":
    default:
      return (
        <ConstituencyReportView
          constName={constName}
          areaCode={areaCode}
          mpName={extDataBlurb?.mp_name}
          party={extDataBlurb?.mp_party}
          roles={extDataBlurb?.mp_roles}
          majority={extDataBlurb?.majority}
          demographics={demographics}
          mapImages={mapImages}
        />
      );
  }
}

export default function MapReportPreview({
  areaCode,
  constName,
  reportType = "default",
  onReportTypeChange,
  clientSlug,
  extDataBlurb,
  demographics,
  mapImages,
  onOpen,
  questionGroupId,       // the map's currently-selected question (for the Custom tab)
  questionLabel,
  onOpenDrilldown,       // (groupId) => open the studio on a drill-down for this question
}) {
  const [tab, setTab] = useState("default");
  const [minimized, setMinimized] = useState(true);

  if (!areaCode) return null;

  if (minimized) {
    return (
      <Box
        onClick={() => setMinimized(false)}
        role="button"
        aria-label="Show report preview"
        sx={{
          position: "absolute",
          left: 16,
          bottom: 16,
          zIndex: 10,
          ...GLASS,
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          px: 1.5,
          py: 1,
          cursor: "pointer",
          transition: "all 0.15s ease",
          "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.12)", borderColor: "rgba(15,159,171,0.3)" },
        }}
      >
        <DescriptionRoundedIcon sx={{ fontSize: 16, color: ACCENT }} />
        <Typography sx={{
          fontSize: "11.5px", fontWeight: 700, color: "#374151",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}>
          Report
        </Typography>
        <Typography sx={{
          fontSize: "10px", color: TEXT_FAINT, fontWeight: 500,
          fontFamily: "'DM Sans', system-ui, sans-serif",
          maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {constName}
        </Typography>
      </Box>
    );
  }

  // Resolve the active type (falls back to Political if unknown).
  const selectedType = DEFAULT_REPORTS.some((r) => r.key === reportType) ? reportType : "default";
  // Gate the report types the same way ReportPage does — but always offer the
  // active type, so a category-matched report (e.g. Motability on a motability
  // question) is never gated away and clamped back to Political.
  const isHolidayTaxClient = HOLIDAY_TAX_CLIENTS.includes(clientSlug);
  const visibleReports = DEFAULT_REPORTS.filter((r) => {
    if (r.key === selectedType) return true;
    if (r.hidden) return false;
    if (r.holidayTaxOnly && !isHolidayTaxClient) return false;
    return true;
  });

  return (
    <Box
      sx={{
        position: "absolute",
        left: 16,
        bottom: 16,
        zIndex: 10,
        width: PANEL_W,
        ...GLASS,
        overflow: "hidden",
        fontFamily: "'DM Sans', system-ui, sans-serif",
      }}
    >
      {/* Minimize button */}
      <IconButton
        size="small"
        onClick={(e) => { e.stopPropagation(); setMinimized(true); }}
        aria-label="Minimize report preview"
        sx={{
          position: "absolute", top: 4, right: 4, zIndex: 2,
          width: 22, height: 22, bgcolor: "rgba(0,0,0,0.5)",
          color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 14 }} />
      </IconButton>

      {/* ── Top half: frosted glass window onto the live report ── */}
      <Box
        onClick={onOpen}
        role="button"
        aria-label="Open full report"
        sx={{
          position: "relative",
          width: "100%",
          height: PREVIEW_H,
          overflow: "hidden",
          cursor: "pointer",
          bgcolor: "#fff",
          "&:hover .report-preview-glass": {
            background: "rgba(255,255,255,0.16)",
            backdropFilter: "blur(0.5px)",
            WebkitBackdropFilter: "blur(0.5px)",
          },
          "&:hover .report-preview-open": { opacity: 1, transform: "translateY(0)" },
        }}
      >
        {/* Live report, scaled down to a thumbnail */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: REPORT_W,
            height: REPORT_H,
            transform: `scale(${SCALE})`,
            transformOrigin: "top left",
            pointerEvents: "none",
          }}
        >
          {renderReport(selectedType, { constName, areaCode, extDataBlurb, demographics, mapImages })}
        </Box>

        {/* Frosted glass pane — the "looking into the preview" effect */}
        <Box
          className="report-preview-glass"
          sx={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.28)",
            backdropFilter: "blur(1px)",
            WebkitBackdropFilter: "blur(1px)",
            transition: "background 0.15s ease, backdrop-filter 0.15s ease",
            boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.5)",
          }}
        />

        {/* "Open" affordance, revealed on hover */}
        <Box
          className="report-preview-open"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 0.85,
            py: 0.35,
            borderRadius: "6px",
            bgcolor: "rgba(15,23,42,0.82)",
            color: "#fff",
            fontSize: "10px",
            fontWeight: 700,
            letterSpacing: "0.02em",
            opacity: 0,
            transform: "translateY(-2px)",
            transition: "opacity 0.15s ease, transform 0.15s ease",
            pointerEvents: "none",
          }}
        >
          <OpenInFullRoundedIcon sx={{ fontSize: 11 }} />
          Open
        </Box>

        {/* Bottom-left caption over the glass */}
        <Typography
          sx={{
            position: "absolute",
            left: 10,
            bottom: 8,
            fontSize: "9.5px",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "#475569",
            textShadow: "0 1px 2px rgba(255,255,255,0.7)",
          }}
        >
          Report preview
        </Typography>
      </Box>

      {/* ── Bottom half: tabbed selector ── */}
      <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.07)" }}>
        {/* Tab row: Default | Custom */}
        <Box sx={{ display: "flex", gap: 0.5, px: 1, pt: 1 }}>
          {[
            { key: "default", label: "Default" },
            { key: "custom", label: "Custom" },
          ].map((t) => {
            const active = tab === t.key;
            return (
              <Box
                key={t.key}
                onClick={() => setTab(t.key)}
                sx={{
                  flex: 1,
                  textAlign: "center",
                  py: 0.5,
                  borderRadius: "7px",
                  fontSize: "11.5px",
                  fontWeight: active ? 700 : 600,
                  cursor: "pointer",
                  userSelect: "none",
                  color: active ? ACCENT : TEXT_FAINT,
                  bgcolor: active ? "rgba(15,159,171,0.08)" : "transparent",
                  border: "1px solid",
                  borderColor: active ? ACCENT : "rgba(0,0,0,0.08)",
                  transition: "all 0.12s ease",
                  "&:hover": active ? {} : { color: ACCENT, borderColor: ACCENT },
                }}
              >
                {t.label}
              </Box>
            );
          })}
        </Box>

        {/* Tab content */}
        <Box sx={{ px: 1.25, pt: 1, pb: 1.25 }}>
          {tab === "default" ? (
            <>
              <Typography
                sx={{
                  fontSize: "9.5px",
                  fontWeight: 700,
                  color: TEXT_FAINT,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.5,
                }}
              >
                Report type
              </Typography>
              <Select
                value={selectedType}
                onChange={(e) => onReportTypeChange?.(e.target.value)}
                size="small"
                fullWidth
                MenuProps={{ PaperProps: { sx: { borderRadius: "8px", mt: 0.5 } } }}
                sx={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  fontSize: "12.5px",
                  fontWeight: 600,
                  color: "#1a1d23",
                  bgcolor: "#fff",
                  borderRadius: "8px",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(0,0,0,0.12)" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ACCENT },
                  "& .MuiSelect-select": { py: 0.85, px: 1.1 },
                }}
              >
                {visibleReports.map((r) => (
                  <MenuItem key={r.key} value={r.key} sx={{ fontSize: "12.5px", fontWeight: 600 }}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>

              {/* CTA — opens the full report for the selected type */}
              <Box
                onClick={onOpen}
                role="button"
                aria-label={`See the report on ${constName || "this constituency"}`}
                sx={{
                  mt: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1,
                  py: 0.85,
                  borderRadius: "8px",
                  bgcolor: "rgba(15,159,171,0.08)",
                  border: "1px solid rgba(15,159,171,0.25)",
                  color: ACCENT_DARK,
                  cursor: "pointer",
                  transition: "all 0.12s ease",
                  "&:hover": { bgcolor: "rgba(15,159,171,0.15)", borderColor: ACCENT },
                }}
              >
                <Typography sx={{ flex: 1, fontSize: "11.5px", fontWeight: 600, lineHeight: 1.3 }}>
                  Click here to see a report on{" "}
                  <Box component="span" sx={{ fontWeight: 800 }}>
                    {constName || "this constituency"}
                  </Box>
                </Typography>
                <ArrowForwardRoundedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
              </Box>
            </>
          ) : (
            /* Custom tab — drill into the map's currently-selected question */
            <>
              <Typography
                sx={{
                  fontSize: "9.5px",
                  fontWeight: 700,
                  color: TEXT_FAINT,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em",
                  mb: 0.5,
                }}
              >
                Question drill-down
              </Typography>

              {questionGroupId && questionLabel ? (
                <Box
                  onClick={() => onOpenDrilldown?.(questionGroupId)}
                  role="button"
                  aria-label={`See a drill-down report on ${questionLabel} in ${constName || "this constituency"}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1,
                    py: 0.85,
                    borderRadius: "8px",
                    bgcolor: "rgba(15,159,171,0.08)",
                    border: "1px solid rgba(15,159,171,0.25)",
                    color: ACCENT_DARK,
                    cursor: "pointer",
                    transition: "all 0.12s ease",
                    "&:hover": { bgcolor: "rgba(15,159,171,0.15)", borderColor: ACCENT },
                  }}
                >
                  <Typography sx={{ flex: 1, fontSize: "11.5px", fontWeight: 600, lineHeight: 1.3 }}>
                    See a report on{" "}
                    <Box component="span" sx={{ fontWeight: 800 }}>{questionLabel}</Box>{" "}
                    in{" "}
                    <Box component="span" sx={{ fontWeight: 800 }}>{constName || "this constituency"}</Box>
                  </Typography>
                  <ArrowForwardRoundedIcon sx={{ fontSize: 15, flexShrink: 0 }} />
                </Box>
              ) : (
                <Box
                  sx={{
                    py: 1.5,
                    px: 1,
                    textAlign: "center",
                    borderRadius: "8px",
                    border: "1px dashed rgba(0,0,0,0.14)",
                    bgcolor: "rgba(0,0,0,0.015)",
                  }}
                >
                  <Typography sx={{ fontSize: "10.5px", color: TEXT_FAINT, lineHeight: 1.4 }}>
                    Select a question on the map to drill into it here.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
